/* global google, x5script */

import { Loader } from '@googlemaps/js-api-loader';

class FindLoanOfficer {
  constructor() {
    this.mapOptions = x5script.map;

    this.decodeHTMLEntities = (text) => {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = text;
      return textArea.value;
    };

    if (!this.mapOptions.apikey) {
      // eslint-disable-next-line no-console
      console.warn('Missing required API Key or Map ID');
      return;
    }

    this.styles = [
      {
        featureType: 'administrative',
        stylers: [
          {
            color: '#a5c5dd',
          },
          {
            weight: 1,
          },
        ],
      },
      {
        featureType: 'administrative',
        elementType: 'geometry',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'administrative',
        elementType: 'geometry.stroke',
        stylers: [
          {
            weight: 2,
          },
        ],
      },
      {
        featureType: 'administrative.land_parcel',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'administrative.province',
        elementType: 'geometry.stroke',
        stylers: [
          {
            color: '#2d66a0',
          },
          {
            visibility: 'on',
          },
        ],
      },
      {
        featureType: 'landscape',
        stylers: [
          {
            color: '#4f82b2',
          },
        ],
      },
      {
        featureType: 'poi',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'poi',
        elementType: 'labels.text',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road',
        stylers: [
          {
            color: '#5c95c1',
          },
        ],
      },
      {
        featureType: 'road',
        elementType: 'labels.icon',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road.highway',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road.highway.controlled_access',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'road.local',
        elementType: 'labels',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'transit',
        stylers: [
          {
            visibility: 'off',
          },
        ],
      },
      {
        featureType: 'water',
        stylers: [
          {
            color: '#2d66a0',
          },
        ],
      },
    ];

    this.selectors = {
      map: '#find-officer-map',
      findForm: '.js-find-officer-form',
      findInput: '.js-find-officer-input',
      useCurrentLocationButton: '.js-find-officer-use-location',
    };

    this.DOM = {
      map: document.querySelector(this.selectors.map),
      findForm: document.querySelector(this.selectors.findForm),
      findInput: document.querySelector(this.selectors.findInput),
      useCurrentLocationButton: document.querySelector(
        this.selectors.useCurrentLocationButton,
      ),
    };

    this.classes = {
      hasResults: 'has-results',
      isTouched: 'is-touched',
    };

    this.loader = new Loader({
      apiKey: this.mapOptions.apikey,
      version: 'weekly',
    });

    this.map = {
      object: null,
      infoWindow: null,
      currentMarker: null,
      infoWindowContent: null,
    };

    window.addEventListener('DOMContentLoaded', () => {
      this.init();
    });
  }

  init() {
    this.mapInit();
    this.findInit();
  }

  findInit() {
    if (!this.DOM.findInput) {
      return;
    }

    this.DOM.findInput.addEventListener('focus', () => {
      this.DOM.findForm.classList.add(this.classes.isTouched);
    });

    this.DOM.findInput.addEventListener('keyup', (e) => {
      if (e.keyCode === 13 && e.target.value.trim() !== '') {
        this.DOM.findForm.submit();
      }
    });

    this.DOM.findInput.addEventListener('input', (e) => {
      const { value } = e.target;

      this.DOM.findForm.classList.toggle(
        this.classes.hasResults,
        value.trim().length,
      );
    });

    import('@tarekraafat/autocomplete.js').then(({ default: autoComplete }) => {
      // eslint-disable-next-line new-cap
      const ac = new autoComplete({
        selector: this.selectors.findInput,
        data: {
          src: this.mapOptions.flo.autocomplete,
          keys: ['label', 'zipcode'],
          cache: true,
        },
        resultsList: {
          maxResults: 100,
        },
        resultItem: {
          element: (item, data) => {
            item.classList.add(
              'c-find-officer__search-results-item',
              `is-type-${data.value.type}`,
            );
            // eslint-disable-next-line no-param-reassign
            item.textContent = this.decodeHTMLEntities(data.value.label);
          },
        },
      });

      ac.input.addEventListener('selection', (e) => {
        const { url, label } = e.detail.selection.value;
        e.target.value = this.decodeHTMLEntities(label);

        if (url) {
          window.location.href = url;
        }
      });
    });
  }

  mapInit() {
    if (!this.DOM.map) {
      return;
    }

    this.loader.load().then(() => {
      this.map.object = this.createMap(this.DOM.map);
      this.addMarkers();
      this.bindMapEvents();
      this.useCurrentLocation();

      window.googleMap = this.map.object;
    });
  }

  createMap() {
    return new google.maps.Map(this.DOM.map, {
      center: { lat: 33.5, lng: -84 },
      zoom: 6,
      minZoom: 5,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.TOP_RIGHT,
      },
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      styles: this.styles,
    });
  }

  addMarkers() {
    this.infoWindow = new google.maps.InfoWindow({
      pixelOffset: new google.maps.Size(0, 42),
    });

    this.mapOptions.flo.map_features.forEach((item) => {
      const position = {
        lat: item.geometry.coordinates[1],
        lng: item.geometry.coordinates[0],
      };

      const marker = new google.maps.Marker({
        position,
        map: this.map.object,
        title: item.properties.description,
        icon: this.mapOptions.markerIcon,
      });

      marker.addListener('click', () => {
        this.infoWindow.setContent(item.properties.description);
        this.infoWindow.open(this.map.object, marker);
      });
    });
  }

  bindMapEvents() {
    google.maps.event.addListener(this.map.object, 'click', () => {
      this.infoWindow.close();
    });
  }

  getCurrentLocation() {
    return {
      success: (position) => {
        const { coords } = position;

        this.map.object.panTo(
          new google.maps.LatLng(coords.latitude - 0.22, coords.longitude),
        );
        this.map.object.setZoom(11);

        this.DOM.map.scrollIntoView({ behavior: 'smooth' });
      },
      error: (error) => {
        // eslint-disable-next-line no-console
        console.warn({
          errorcode: error.code,
          errormessage: error.message,
        });
      },
    };
  }

  useCurrentLocation() {
    this.DOM.useCurrentLocationButton.addEventListener('click', () => {
      navigator.geolocation.getCurrentPosition(
        this.getCurrentLocation().success,
        this.getCurrentLocation().error,
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        },
      );
    });
  }
}

export default new FindLoanOfficer();
